import { useEffect, useState } from 'react'

import { Flex } from '@react-css/flex'
import mixpanel from 'mixpanel-browser'

import {
  fetchImage,
  fetchPointImage,
} from '../../../../../../api/buildingFetches'
import iconBackDark from '../../../../../../assets/images/icon_back_dark.svg'
import iconCalendar from '../../../../../../assets/images/icon_calendar.svg'
import iconForward from '../../../../../../assets/images/icon_forward.svg'
import { useBuildingContext } from '../../../../../../contexts/buildingContext'
import { useNavigation } from '../../../../../../hooks/useNavigation'
import {
  buildBuildingImageData,
  createObjectURLSafe,
} from '../../../../../../utils/imageUtils'
import { Icon } from '../../../../../common/Icon'
import { LoadingIndicator } from '../../../../../common/LoadingIndicator'
import { useImageViewerContext } from '../../../imageViewerContext'

interface IFullscreenTimeNavigationProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper point type
  point: any
  type: string
  min?: Date
  max?: Date
}
export const FullScreenTimeNavigation = ({
  point,
  type,
  min,
  max,
}: IFullscreenTimeNavigationProps) => {
  const { state: buildingState, updateImage } = useBuildingContext()
  const {
    state: ImageViewerState,
    updatePane2,
    updateMaster,
  } = useImageViewerContext()
  const updateMethod = type === 'master' ? updateMaster : updatePane2
  const { navigateToPointFullscreen } = useNavigation()
  const [previousImage, setPreviousImage] = useState<number | null>(null)
  const [nextImage, setNextImage] = useState<number | null>(null)
  const [currentIndex, setCurrentIndex] = useState<number | null>(null)
  const [localLoad, setLocalLoad] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper sorted type
  const [sorted, setSorted] = useState<any>([])

  const getDateString = () => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    const registeredDate = new Date(ImageViewerState[type].data?.taken_on)
    const day = registeredDate.getDate()
    const month = monthNames[registeredDate.getMonth()]
    const year = registeredDate.getFullYear()
    return `${month} ${day}, ${year}`
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
  const getSetCurrentImageData = async (image: any) => {
    const oldAngle = ImageViewerState[type].data.angle
    const newAngle = image.angle
    const viewpointsImage = await fetchPointImage(
      buildingState.projectId,
      buildingState.floorData.floor_code,
      image.id,
    )
    if (!viewpointsImage) {
      setLocalLoad(false)
      return
    }
    const imageBlob = await fetchImage(viewpointsImage.processed_image_url)
    const imageUrl = createObjectURLSafe(imageBlob)
    if (!imageUrl) {
      setLocalLoad(false)
      return
    }
    const currentImageData = {
      image: imageUrl,
      data: image,
      angleDelta: newAngle - oldAngle,
      observationDate: image.taken_on,
      isMostRecent: true,
    }
    mixpanel.track('Minimap Time Navigation')
    navigateToPointFullscreen(image.sub_point_id, { date: image.taken_on })
    updateMethod(currentImageData)

    // Keep building image in sync for TagManager hydrate + SafetyTag create.
    // Only master: pane2 date changes must not stomp site context.
    if (type === 'master') {
      const previousBuildingUrl = buildingState.imageData?.image as
        | string
        | undefined
      const buildingImageData = await buildBuildingImageData({
        selectedImage: image,
        viewpointsImage,
        imageUrl,
      }).catch(() => null)
      if (buildingImageData) {
        updateImage(buildingImageData)
      }
      if (
        previousBuildingUrl &&
        previousBuildingUrl !== imageUrl &&
        previousBuildingUrl.startsWith('blob:')
      ) {
        URL.revokeObjectURL(previousBuildingUrl)
      }
    }

    setLocalLoad(false)
  }

  const onClickNext = () => {
    if (nextImage !== null) {
      // If max is set, check if next image is within max
      if (max) {
        const nextTakenOn = new Date(sorted[nextImage]?.taken_on)
        if (nextTakenOn > max) return
      }
      setLocalLoad(true)
      setCurrentIndex(nextImage)
      getSetCurrentImageData(sorted[nextImage])
    }
  }

  const onClickPrev = () => {
    if (previousImage !== null) {
      // If min is set, check if previous image is within min
      if (min) {
        const prevTakenOn = new Date(sorted[previousImage]?.taken_on)
        if (prevTakenOn < min) return
      }
      setLocalLoad(true)
      setCurrentIndex(previousImage)
      getSetCurrentImageData(sorted[previousImage])
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper images type
  const sortImages = (images: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image types
    return [...images].sort((a: any, b: any) =>
      new Date(a.taken_on).getTime() > new Date(b.taken_on).getTime() ? 1 : -1,
    )
  }

  useEffect(() => {
    if (currentIndex !== null) {
      if (currentIndex! > 0) {
        setPreviousImage(currentIndex - 1)
      } else {
        setPreviousImage(null)
      }
      if (currentIndex! < sorted.length - 1) {
        setNextImage(currentIndex + 1)
      } else {
        setNextImage(null)
      }
    }
    // eslint-disable-next-line
  }, [currentIndex, sorted])

  useEffect(() => {
    let sorts = sortImages(point.images)
    // If min/max are set, filter images to within range
    if (min || max) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper img type
      sorts = sorts.filter((img: any) => {
        const taken = new Date(img.taken_on)
        if (min && taken < min) return false
        if (max && taken > max) return false
        return true
      })
    }
    setSorted(sorts)
    if (sorts.length < 2) {
      setNextImage(null)
      setPreviousImage(null)
    }
    for (const p in sorts) {
      if (sorts[p]?.id === ImageViewerState[type].data?.id) {
        setCurrentIndex(parseInt(p))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: ImageViewerState and type intentionally omitted - complex expression in dependency array
  }, [ImageViewerState[type], point, min, max])

  return (
    <Flex
      flexDirection="row"
      style={{ alignItems: 'center', justifyContent: 'center' }}
    >
      {localLoad && (
        <span style={{ margin: '0 5px' }}>
          <LoadingIndicator fb />
        </span>
      )}
      <Flex style={{ width: '25px', alignItems: 'center', height: '100%' }}>
        {previousImage !== null && (
          <Icon
            icon={iconBackDark}
            size={14}
            style={{ marginRight: '9px', cursor: 'pointer', margin: 'auto' }}
            onClick={onClickPrev}
          />
        )}
      </Flex>
      <Icon icon={iconCalendar} size={14} style={{ marginRight: '9px' }} />
      <span className="minimap-date">{getDateString()}</span>
      <Flex style={{ width: '25px', alignItems: 'center' }}>
        {nextImage !== null && (
          <Icon
            icon={iconForward}
            size={14}
            style={{ marginLeft: '9px', cursor: 'pointer', margin: 'auto' }}
            onClick={onClickNext}
          />
        )}
      </Flex>
    </Flex>
  )
}
